import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance, { axiosInstanceGis } from "../api";
import { QueryState, Result } from "../interfaces/queryBuilderInterfaces";
import {
  currActiveTab,
  setCurrentQueryIndex,
  setCurrentQueryType,
  setHistoryCount,
  setLoading,
  setQuerySubType,
  setSourceFlag,
} from "./queryBuilderSlice";
import { useSelector } from "react-redux";

interface GoogleMapState {
  apiKey: string | null;
  result: Result | null;
  geoJson: any | null;
  geoKey: string | null;
  loading: boolean;
  error: string | null;
  isNewQuery: boolean;
}

const singleMapQueryState: GoogleMapState = {
  apiKey: null,
  result: null,
  geoJson: null,
  geoKey: null,
  loading: false,
  error: null,
  isNewQuery: true,
};

interface InitialQueryState {
  mapQueries: GoogleMapState[];
  activeIndex: number;
}

const initialState: InitialQueryState = {
  mapQueries: [{ ...singleMapQueryState }],
  activeIndex: 0,
};

interface SingleQueryState {
  query: { name: string };
}
interface RootState {
  queryBuilder: {
    queries: SingleQueryState[];
    currentTabIndex: number;
  };
  map: GoogleMapState;
}

export const getGoogleMapApiKey = createAsyncThunk(
  "user/googleKeyOrClient",
  async (currentQueryIndex: number) => {
    const response = await axiosInstance.get("/rest/geo/googleKeyOrClient");
    return response.data;
  },
);

export const getMapResult = createAsyncThunk<
  any,
  { selectedMap: string; currentTabIndex: number }
>("olapquery/map", async ({ selectedMap, currentTabIndex }, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;

  const queryName = state.queryBuilder.queries[currentTabIndex].query.name;
  const response = await axiosInstance.get(
    `/rest/olapquery/${queryName}/result/map/${selectedMap}`,
  );
  if (response.status === 200) {
    thunkAPI.dispatch(setSourceFlag({ flag: false, currentTabIndex }));
  }
  if (response?.data?.activeIndex) {
    thunkAPI.dispatch(
      setCurrentQueryIndex({
        activeIndex: response.data.activeIndex,
        currentTabIndex,
      }),
    );
  }
  if (response.data.history) {
    thunkAPI.dispatch(
      setHistoryCount({
        historyLength: response.data.history.length,
        currentTabIndex,
      }),
    );
  }
  if (response.data.activeQueryDetail?.queryMode) {
    thunkAPI.dispatch(
      setCurrentQueryType({
        type: response.data.activeQueryDetail?.queryMode,
        currentTabIndex,
      }),
    );
  }
  if (
    response.data.query.properties.subType &&
    response.data.query.properties.subType != ""
  ) {
    thunkAPI.dispatch(
      setQuerySubType({
        type: response.data.query.properties.subType,
        currentTabIndex,
      }),
    );
  }
  return response.data;
});
interface GeoJsonError {
  message: string;
  error?: unknown;
}
export const getMapGeoJson = createAsyncThunk<
  { dataObject: any },
  { geoReq: any; currentTabIndex: number },
  { rejectValue: GeoJsonError }
>("googleMap/getGeoJson", async ({ geoReq, currentTabIndex }, thunkAPI) => {
  const chain: any = {};
  const level = geoReq.req;
  try {
    const serviceUrlResponse = await axiosInstance.get(`/rest/geo/serviceUrl`);
    chain.serviceUrl = serviceUrlResponse.data;

    const data1 = await MapService.getSignature(
      `/rest/geo/gis/sig/`,
      "/geo/key/" + level,
    );
    chain.data1 = data1;

    const data2 = await MapService.getSignature(
      `/rest/geo/gis/sig/`,
      "/metric/key",
    );
    chain.data2 = data2;

    const postSignature_1 = `${chain.serviceUrl}/geo/key/${level}?signature=${chain.data1}`;
    const data3 = await MapService.postSignatureMVRKey(
      postSignature_1,
      geoReq.filterCodes,
    );
    chain.data3 = data3;
    thunkAPI.dispatch(mapSlice.actions.setGeoKey({ data3, currentTabIndex }));

    const postSignature_2 = `${chain.serviceUrl}/metric/key?signature=${chain.data2}`;
    const data4 = await MapService.postSignatureMetricKey(
      postSignature_2,
      geoReq.metricKeyValues,
    );
    chain.data4 = data4;

    const data5 = await MapService.getSignature(
      `/rest/geo/gis/sig/`,
      "/thematic/" + chain.data3,
    );
    chain.data5 = data5;

    if (geoReq.mapType === "thematic") {
      const url = `${chain.serviceUrl}/thematic/${chain.data3}?metric=${chain.data4}:TCVI&signature=${chain.data5}`;
      const geoData = await MapService.mapGeoJsonData(url);
      return { dataObject: geoData };
    } else {
      const data6 = await MapService.getSignature(
        `/rest/geo/gis/sig/`,
        "/dotdensity/" + chain.data3,
      );
      chain.data6 = data6;

      const doturl = `${chain.serviceUrl}/dotdensity/${chain.data3}?metric=${chain.data4}:DDEFAULT&signature=${chain.data6}`;
      const dotgeoData = await MapService.mapGeoJsonData(doturl);

      const thematicurl = `${chain.serviceUrl}/thematic/${chain.data3}?metric=${chain.data4}:TGREYBORDER&signature=${chain.data5}`;
      const thematicgeoData = await MapService.mapGeoJsonData(thematicurl);

      return { dataObject: { dotgeoData, thematicgeoData } };
    }
  } catch (e: any) {
    let errorMsg =
      "Unable to load the map at this time. Please try again later.";
    return thunkAPI.rejectWithValue({
      message: errorMsg,
      error: e,
    });
  }
});

export const getMapGeoJsonStylesOnly = createAsyncThunk<any, any>(
  "googleMap/getStylesOnly",
  async ({ geoReq, currentTabIndex }, thunkAPI) => {
    const chain: any = {};

    const geoKey = (thunkAPI.getState() as RootState).map.geoKey;

    try {
      const data1 = await MapService.getSignature(
        `/rest/geo/gis/sig/`,
        "/metric/key",
      );
      chain.data1 = data1;

      const postSignature_2 = `/metric/key?signature=${chain.data1}`;
      const data2 = await MapService.postSignatureMetricKey(
        postSignature_2,
        geoReq.metricKeyValues,
      );
      chain.data2 = data2;

      const data3 = await MapService.getSignature(
        `/rest/geo/gis/sig/`,
        "/thematic/stylesonly/" + geoKey,
      );
      chain.data3 = data3;

      if (geoReq.mapType === "thematic") {
        const url = `/thematic/stylesonly/${geoKey}?metric=${chain.data2}:TCVI&signature=${chain.data3}`;
        const geoData = await MapService.mapGeoJsonData(url);
        return { dataObject: geoData };
      }
    } catch (e) {
      return { dataObject: {} };
    }
  },
);

export const selectCurrentMapQuery = (state: any) =>
  state.map.mapQueries[state.map.activeIndex];

const getCurrentIndex = (state: any) => state.mapQueries[state.activeIndex];

const mapSlice = createSlice({
  name: "map",
  initialState,

  reducers: {
    setGeoKey: (state, action) => {
      state.mapQueries[action.payload.currentTabIndex].geoKey =
        action.payload.data3;
    },
    setMapData: (state, action) => {
      state.mapQueries[action.payload.currentTabIndex].result =
        action.payload.data;
    },
    setMapLoading(
      state,
      action: PayloadAction<{ flag: boolean; currentTabIndex: number }>,
    ) {
      state.mapQueries[action.payload.currentTabIndex].loading =
        action.payload.flag;
    },
    changeMapTab: (state, action) => {
      state.mapQueries[action.payload].isNewQuery = false;
    },
    removeMapquery(state, action) {
      state.mapQueries.splice(action.payload, 1);
    },
    resetMapSlice(state) {
      state.mapQueries = [{ ...singleMapQueryState }];
    },
    addNewMapQuery(state) {
      state.mapQueries.push({
        ...singleMapQueryState,
      });
    },
    cloneMapQuery(state, action) {
      state.mapQueries.push({
        ...state.mapQueries[action.payload],
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGoogleMapApiKey.pending, (state, action) => {
        const currentTabIndex = action.meta.arg;

        state.mapQueries[currentTabIndex].loading = true;
        state.mapQueries[currentTabIndex].error = null;
      })
      .addCase(getGoogleMapApiKey.fulfilled, (state, action) => {
        const currentTabIndex = action.meta.arg;

        state.mapQueries[currentTabIndex].apiKey = action.payload;
        state.mapQueries[currentTabIndex].loading = false;
      })
      .addCase(getGoogleMapApiKey.rejected, (state, action) => {
        const currentTabIndex = action.meta.arg;

        state.mapQueries[currentTabIndex].loading = false;
        state.mapQueries[currentTabIndex].error =
          action.error.message || "Failed to fetch API key";
      })
      .addCase(getMapResult.pending, (state, action) => {
        const { currentTabIndex } = action.meta.arg;
        state.mapQueries[currentTabIndex].loading = true;
        state.mapQueries[currentTabIndex].error = null;
      })
      .addCase(getMapResult.fulfilled, (state, action) => {
        const { currentTabIndex } = action.meta.arg;
        state.mapQueries[currentTabIndex].isNewQuery = true;
        state.mapQueries[currentTabIndex].result = action.payload;

        state.mapQueries[currentTabIndex].loading = false;
      })
      .addCase(getMapResult.rejected, (state, action) => {
        const { currentTabIndex } = action.meta.arg;

        state.mapQueries[currentTabIndex].loading = false;
        state.mapQueries[currentTabIndex].error =
          action.error.message || "Failed to fetch Map result";
      })
      .addCase(getMapGeoJson.pending, (state, action) => {
        const { currentTabIndex } = action.meta.arg;

        state.mapQueries[currentTabIndex].loading = true;
        state.mapQueries[currentTabIndex].error = null;
      })
      .addCase(getMapGeoJson.fulfilled, (state, action) => {
        const { currentTabIndex } = action.meta.arg;

        state.mapQueries[currentTabIndex].geoJson = action.payload;
        state.mapQueries[currentTabIndex].loading = false;
      })
      .addCase(getMapGeoJson.rejected, (state, action) => {
        const { currentTabIndex } = action.meta.arg;
        state.mapQueries[currentTabIndex].loading = false;
        state.mapQueries[currentTabIndex].error =
          action.error.message || "Failed to fetch GeoJSON data";
      })
      .addCase(getMapGeoJsonStylesOnly.pending, (state, action) => {
        const { currentTabIndex } = action.meta.arg;
        state.mapQueries[currentTabIndex].loading = true;
        state.mapQueries[currentTabIndex].error = null;
      })
      .addCase(getMapGeoJsonStylesOnly.fulfilled, (state, action) => {
        const { currentTabIndex } = action.meta.arg;
        state.mapQueries[currentTabIndex].geoJson = action.payload;
        state.mapQueries[currentTabIndex].loading = false;
      })
      .addCase(getMapGeoJsonStylesOnly.rejected, (state, action) => {
        const { currentTabIndex } = action.meta.arg;
        state.mapQueries[currentTabIndex].loading = false;
        state.mapQueries[currentTabIndex].error =
          action.error.message || "Failed to fetch GeoJSON Styles only data";
      });
  },
});
export const {
  setGeoKey,
  setMapData,
  addNewMapQuery,
  removeMapquery,
  changeMapTab,
  cloneMapQuery,
  resetMapSlice,
  setMapLoading,
} = mapSlice.actions;
export default mapSlice.reducer;

class MapService {
  static serviceUrls = {
    getGisUrl: "rest/geo/serviceUrl",
  };

  static getSignature = async (url: any, urlToEncrypt: any) => {
    const data = { toEncrypt: urlToEncrypt };
    const response = await axiosInstance.post(url, data, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  };

  static postSignatureMVRKey = async (url: any, filterCodes: any) => {
    let filterCodesFormData = new URLSearchParams();
    for (let i = 0; i < filterCodes.length; i++) {
      filterCodesFormData.append("filterCodes[]", filterCodes[i]);
    }
    const response = await axiosInstanceGis.post(url, filterCodesFormData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  };

  static postSignatureMetricKey = async (url: any, metricKeyValues: any) => {
    const response = await axiosInstanceGis.post(url, metricKeyValues, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  };

  static mapGeoJsonData = async (url: any) => {
    const response = await axiosInstanceGis.get(url);
    return response.data;
  };
}
